import {
  FormContext,
  FormPanel,
  useFormSelector,
  FormTextField,
  FormCheckbox,
  FormAutocomplete,
  FormCustomField,
  TextField,
  ApiFilterOperation,
  Params,
} from '@eas/common-web';
import React, { useContext, useEffect } from 'react';
import { DatedFields } from '../../components/evidence/dated-evidence/dated-fields';
import { NotedEvidenceFieldsProps } from '../../components/evidence/noted-evidence/noted-types';

import { MinimalDistances, OvzFacility } from '../../models';
import { LocationPanel } from '../../components/form/location-panel/location-panel';
import { HelpLabel } from '../../components/help/help-label';

import {
  useMinimalDistancesCategoryAutocomplete,
  useMinimalDistancesDeterminationMethodAutocomplete,
  useOVZFacilityDetail,
} from './minimal-distances-api';

import { AddressField } from '../../components/form/address-field/address-field';
import { DetailNavigationButton } from '../../components/detail-navigation-button/detail-navigation-button';
import { EvidenceUrl } from '../../enums';

import { dd2dms } from '../../utils/latlng';
import { noop } from 'lodash';
import _ from 'lodash';
import { dictionaryLabelMapper } from '../../components/form/mappers/label-mappers';

export const formatFacilityCoordinates = (ovzFacility?: OvzFacility) => {
  let lat = '-';
  let lon = '-';
  if (!ovzFacility) {
    return { lat, lon };
  }
  if (ovzFacility.wgs84Coordinates?.lat) {
    const parsed = dd2dms(ovzFacility.wgs84Coordinates.lat);
    lat = `${parsed.degrees}°${parsed.minutes}'${parsed.seconds}"`;
  }

  if (ovzFacility.wgs84Coordinates?.lon) {
    const parsed = dd2dms(ovzFacility.wgs84Coordinates.lon);
    lon = `${parsed.degrees}°${parsed.minutes}'${parsed.seconds}"`;
  }
  return { lat, lon };
};

const getOperatorIco = (operator?: OvzFacility['operator']) => {
  const ico = _.get(operator, 'ico');
  if (!ico) {
    return '';
  }
  if (typeof ico === 'string') {
    return ico;
  }
  return _.get(ico, 'value');
};

const getLivestockFilter = (ovzFacility?: OvzFacility): Params | undefined => {
  if (!ovzFacility || !ovzFacility?.withOnlyLivestockSource) {
    return undefined;
  }
  return {
    filters: [
      {
        field: 'code',
        operation: ApiFilterOperation.START_WITH,
        value: '8.',
      },
    ],
  };
};

export function MinimalDistancesFields(props: NotedEvidenceFieldsProps) {
  const { setFieldValue } = useContext(FormContext);

  const determinationAutocomplete = useMinimalDistancesDeterminationMethodAutocomplete();

  const { wgs84Coordinates, ovzFacility } = useFormSelector(
    (data: MinimalDistances) => ({
      wgs84Coordinates: data.wgs84Coordinates,
      ovzFacility: data.ovzFacility,
    })
  );

  const { result: ovzFacilityDetail } = useOVZFacilityDetail(ovzFacility?.id);
  const { lat: ovzLat, lon: ovzLon } = formatFacilityCoordinates(
    ovzFacilityDetail
  );

  const categoryAutocomplete = useMinimalDistancesCategoryAutocomplete(
    getLivestockFilter(ovzFacilityDetail)
  );

  useEffect(() => {
    setFieldValue('ovzFacility', ovzFacilityDetail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ovzFacilityDetail?.id]);

  return (
    <>
      <FormPanel label="Základní informace" expandable defaultExpanded={true}>
        <FormTextField
          name="sourceId"
          label={
            <HelpLabel
              label="Číslo zdroje"
              code="MINIMAL_DISTANCES_SOURCE_ID"
              required
            />
          }
        />
        <FormTextField
          name="name"
          label={
            <HelpLabel
              label="Název zdroje"
              code="MINIMAL_DISTANCES_SOURCE_NAME"
              required
            />
          }
        />

        <FormAutocomplete
          name="sourceCategory"
          source={categoryAutocomplete}
          label={
            <HelpLabel
              label="Zařazení zdroje podle přílohy č. 2a zákona"
              code="MINIMAL_DISTANCES_SOURCE_CATEGORY"
              required
            />
          }
          labelMapper={dictionaryLabelMapper}
          notifyChange={async (value) => {
            // dont mind if item is undefined, clearing the field is what we want in that case
            setFieldValue(
              'determinationMethod',
              _.get(value, 'determinationMethod')
            );
          }}
        />
        <FormAutocomplete
          name="determinationMethod"
          source={determinationAutocomplete}
          disabled
          label={
            <HelpLabel
              label="Způsob určení minimální vzdálenosti podle § 27d"
              code="MINIMAL_DISTANCES_DETERMINATION_METHOD"
              required
            />
          }
          labelMapper={dictionaryLabelMapper}
        />
        <FormCheckbox
          name="exemptFromMinDistances"
          label={
            <HelpLabel
              label="Výjimka z MV"
              code="MINIMAL_DISTANCES_EXEMPT_FROM_MIN_DISTANCES"
            />
          }
        />
      </FormPanel>

      <FormPanel
        label="Adresa zdroje (provozovna OVZ)"
        expandable
        defaultExpanded={false}
      >
        <AddressField name="ovzFacility.address" disabled />
        <FormCustomField
          label={
            <HelpLabel
              label="Zeměpisná šířka provozovny"
              code="LOCATION_LATITUDE"
            />
          }
        >
          <TextField value={ovzLat} onChange={noop} disabled />
        </FormCustomField>
        <FormCustomField
          label={
            <HelpLabel
              label="Zeměpisná délka provozovny"
              code="LOCATION_LONGITUDE"
            />
          }
        >
          <TextField value={ovzLon} onChange={noop} disabled />
        </FormCustomField>
      </FormPanel>

      <LocationPanel
        locationName="wgs84Coordinates"
        locationValue={wgs84Coordinates}
        addressPanelLabel="Souřadnice zdroje"
        locationPanelLabel="Poloha zdroje"
        showCoordinates={true}
        showMap={true}
        showAddress={false}
        showLocationPanel={true}
        showCoordinatesDistance={false}
        // Markers={() => (
        //   <OvzMapMarkers
        //     items={
        //       ovzFacility && ovzFacility.wgs84Coordinates
        //         ? [
        //             {
        //               ...ovzFacility.wgs84Coordinates!,
        //               id: ovzFacility?.id,
        //             },
        //           ]
        //         : []
        //     }
        //   />
        // )}
        locationToolbarProps={{
          showFillAddressBtn: false,
          showFillMapBtn: false,
        }}
        expandable
        defaultExpanded={false}
      />

      {/************************************************/}

      <FormPanel
        label="Informace o provozovně, na které je zdroj veden"
        expandable
        defaultExpanded={false}
      >
        <FormTextField
          name="ovzFacility.facilityId"
          label="IČP provozovny"
          disabled
        />
        <FormTextField
          name="ovzFacility.name"
          label="Název provozovny"
          disabled
          after={
            <DetailNavigationButton
              url={EvidenceUrl.OVZ_FACILITIES}
              id={ovzFacility?.id}
            />
          }
        />

        <FormCustomField
          name="ovzFacility.operator.ico"
          label="IČO provozovatele provozovny"
          disabled
        >
          <TextField
            value={getOperatorIco(ovzFacility?.operator)}
            onChange={noop}
            disabled
          />
        </FormCustomField>

        <FormTextField
          name="ovzFacility.operator.name"
          label="Název provozovatele provozovny"
          after={
            <DetailNavigationButton
              url={`${EvidenceUrl.SUBJECTS}`}
              id={ovzFacility?.operator?.id}
            />
          }
          disabled
        />
      </FormPanel>

      {/************************************************/}

      {props.Notes}
      {props.HistoryRecords}
      <FormPanel label="Obecné" expandable defaultExpanded={false}>
        <FormCheckbox name="active" label="Aktivní" disabled />
        <DatedFields />
      </FormPanel>
    </>
  );
}
